// redux/slices/notificationsSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

//const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4005";
const API_URL = process.env.REACT_APP_API_URL;

// Función para guardar IDs de notificaciones leídas en localStorage
const saveReadNotification = (notificationId) => {
  try {
    // Obtener notificaciones leídas del localStorage
    const readNotificationsStr = localStorage.getItem('readNotifications') || '[]';
    const readNotifications = JSON.parse(readNotificationsStr);
    
    // Añadir la nueva notificación leída si no existe ya
    if (!readNotifications.includes(notificationId)) {
      readNotifications.push(notificationId);
      // Guardar en localStorage
      localStorage.setItem('readNotifications', JSON.stringify(readNotifications));
    }
  } catch (error) {
    console.error('Error al guardar notificación leída en localStorage:', error);
  }
};

// Función para obtener IDs de notificaciones leídas de localStorage
export const getReadNotifications = () => {
  try {
    const readNotificationsStr = localStorage.getItem('readNotifications') || '[]';
    return JSON.parse(readNotificationsStr);
  } catch (error) {
    console.error('Error al obtener notificaciones leídas de localStorage:', error);
    return [];
  }
};

// AsyncThunk para obtener notificaciones del usuario
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return [];

      // Decodificar el token para obtener el ID del usuario y rol
      let currentUserId;
      let userRole;
      try {
        const tokenPayload = token.split('.');
        const decodedPayload = JSON.parse(atob(tokenPayload[1]));
        currentUserId = decodedPayload.id;
        userRole = decodedPayload.role;
      } catch (error) {
        console.error("Error al decodificar el token:", error);
        return [];
      }

      const notifications = [];
      
      // Si el usuario es admin, obtener notificaciones específicas para administradores
      if (userRole === "admin") {
        try {
          // 1. Obtener todos los usuarios para detectar nuevos registros
          const usersResponse = await axios.get(
            `${API_URL}/api/v1/users`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          
          // Procesar solo los usuarios registrados en los últimos 7 días
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          
          const recentUsers = usersResponse.data.filter(user => {
            const creationDate = new Date(user.createdAt);
            return creationDate > sevenDaysAgo;
          });
          
          // Crear notificaciones para nuevos usuarios
          recentUsers.forEach(user => {
            const creationDate = new Date(user.createdAt);
            notifications.push({
              id: `new-user-${user.id}`,
              type: "info",
              message: `Nuevo usuario registrado: ${user.name}`,
              read: false,
              date: creationDate.toISOString(), // Convertir a string ISO para serialización
              userId: user.id,
              userName: user.name,
              userEmail: user.email,
              isNewUser: true
            });
          });
          
          // 2. Obtener todas las suscripciones pendientes
          const pendingResponse = await axios.get(
            `${API_URL}/api/v1/userSubscriptions?status=pending`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          
          // Para cada suscripción pendiente, obtener detalles del usuario y del plan
          const pendingSubs = pendingResponse.data;
          
          // Procesar solo las suscripciones de los últimos 30 días
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          for (const sub of pendingSubs) {
            const creationDate = new Date(sub.createdAt);
            if (creationDate > thirtyDaysAgo) {
              try {
                // Obtener detalles del usuario
                const userResponse = await axios.get(
                  `${API_URL}/api/v1/users/${sub.userId}`,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                  }
                );
                
                // Obtener detalles del plan
                const planResponse = await axios.get(
                  `${API_URL}/api/v1/subscriptionPlans/${sub.subscriptionPlanId}`,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                  }
                );
                
                notifications.push({
                  id: `admin-pending-${sub.id}`,
                  type: "subscription",
                  message: `${userResponse.data.name} ha solicitado el plan ${planResponse.data.name}`,
                  read: false,
                  date: creationDate.toISOString(), // Convertir a string ISO para serialización
                  subscriptionId: sub.id,
                  userId: sub.userId,
                  userName: userResponse.data.name,
                  planId: sub.subscriptionPlanId,
                  planName: planResponse.data.name,
                  isSubscriptionRequest: true
                });
              } catch (error) {
                console.error("Error al obtener detalles de suscripción pendiente:", error);
              }
            }
          }
        } catch (error) {
          console.error("Error al obtener suscripciones pendientes:", error);
        }
      }
      
      // Obtener las suscripciones del usuario actual
      const response = await axios.get(
        `${API_URL}/api/v1/userSubscriptions/${currentUserId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const userSubscriptions = response.data;
      
      // Ordenar por fecha de actualización (createdAt o updatedAt) descendente
      const sortedSubs = [...userSubscriptions].sort((a, b) => 
        new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
      );

      // Buscar suscripciones activas o rechazadas recientes (últimos 7 días)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      sortedSubs.forEach(sub => {
        const updateDate = new Date(sub.updatedAt || sub.createdAt);
        
        if (updateDate > sevenDaysAgo) {
          if (sub.status === "active") {
            notifications.push({
              id: `active-${sub.id}`,
              type: "success",
              message: "¡Tu suscripción ha sido activada! Ya puedes disfrutar de todas las películas.",
              read: false,
              date: updateDate.toISOString(), // Convertir a string ISO para serialización
              subscriptionId: sub.id
            });
          } else if (sub.status === "rejected") {
            notifications.push({
              id: `rejected-${sub.id}`,
              type: "error",
              message: "Tu solicitud de suscripción ha sido rechazada. Por favor, contacta con soporte.",
              read: false,
              date: updateDate.toISOString(), // Convertir a string ISO para serialización
              subscriptionId: sub.id
            });
          } else if (sub.status === "pending") {
            notifications.push({
              id: `pending-${sub.id}`,
              type: "info",
              message: "Tu solicitud de suscripción está pendiente de aprobación.",
              read: false,
              date: updateDate.toISOString(), // Convertir a string ISO para serialización
              subscriptionId: sub.id
            });
          }
        }
      });
      
      // Ordenar todas las notificaciones por fecha (más recientes primero)
      notifications.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Marcar como leídas las notificaciones que ya están guardadas en localStorage
      const readNotifications = getReadNotifications();
      if (readNotifications.length > 0) {
        notifications.forEach(notification => {
          if (readNotifications.includes(notification.id)) {
            notification.read = true;
          }
        });
      }

      return notifications;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al obtener notificaciones"
      );
    }
  }
);



// AsyncThunk para marcar una notificación como leída
export const markNotificationAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (notificationId, { getState }) => {
    // Guardar en localStorage además de actualizar el estado local
    saveReadNotification(notificationId);
    console.log(`Marcando notificación ${notificationId} como leída y guardando en localStorage`);
    return notificationId;
  }
);

// Slice
const notificationsSlice = createSlice({
  name: "notifications",
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearAllNotifications: (state) => {
      // Marcar todas las notificaciones como leídas en lugar de eliminarlas
      state.items.forEach(item => {
        item.read = true;
        // Guardar cada ID en localStorage
        saveReadNotification(item.id);
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Obtener notificaciones
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Marcar como leída
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.items.find(
          (item) => item.id === action.payload
        );
        if (notification) {
          notification.read = true;
        }
      });
  },
});

export const { clearAllNotifications } = notificationsSlice.actions;
export default notificationsSlice.reducer;
